import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Globe, Lightbulb, Lock, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Page, PageHeader } from "@/app/Page";
import { useEnvName, useProjectSlug } from "@/app/context";
import { Button, Card, ErrorState, Field, Input, Select, ShowAsCode } from "@/design";
import { problemOf, useAddDomain, useServiceInstances } from "@/lib/api";
import { config } from "@/lib/config";
import { apexOf, looksLikeHost, normaliseHost } from "../-domains/registrars";
import { Stepper } from "../-domains/Stepper";

export const Route = createFileRoute("/$workspace/$project/domains/new")({
  staticData: { title: "Add a domain" },
  validateSearch: (s: Record<string, unknown>): { host?: string; service?: string } => ({
    ...(typeof s.host === "string" && s.host ? { host: s.host } : {}),
    ...(typeof s.service === "string" && s.service ? { service: s.service } : {}),
  }),
  component: AddDomain,
});

function AddDomain() {
  const project = useProjectSlug();
  const env = useEnvName();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const instances = useServiceInstances(project, env);
  const web = useMemo(() => (instances.data?.data ?? []).filter((i) => i.kind === "web" || i.kind === undefined), [instances.data]);
  const [raw, setRaw] = useState(search.host ?? "");
  const [service, setService] = useState(search.service ?? "");
  const [touched, setTouched] = useState(false);
  const add = useAddDomain(project, env);

  useEffect(() => {
    if (search.host) setRaw(search.host);
  }, [search.host]);
  useEffect(() => {
    if (!service && web[0]) setService(web[0].name);
  }, [web, service]);

  const host = normaliseHost(raw);
  const valid = looksLikeHost(host);
  const isApex = valid && host === apexOf(host);
  const cleaned = valid && raw.trim().toLowerCase() !== host;
  const fieldErr = add.error ? problemOf(add.error).errors?.find((e) => e.path === "hostname")?.message : undefined;
  const localErr = touched && raw && !valid ? "That does not look like a domain. Try something like www.example.co.uk." : undefined;

  const submit = () => {
    setTouched(true);
    if (!valid || !service) return;
    add.mutate(
      { hostname: host, service },
      {
        onSuccess: (d) =>
          void navigate({
            to: "/$workspace/$project/domains/$domain",
            params: (p: Record<string, string>) => ({ ...p, domain: "hostname" in d ? d.hostname : host }) as never,
            search: (s: Record<string, unknown>) => ({ env: s.env }) as never,
          }),
      },
    );
  };

  return (
    <Page width="narrow">
      <Link
        to="/$workspace/$project/settings/domains"
        params={(p) => p as never}
        search={(s) => ({ env: (s as { env?: string }).env }) as never}
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-text"
      >
        <ArrowLeft className="size-4" /> Domains
      </Link>
      <PageHeader title="Add a domain" description="Use your own address, like www.yourbusiness.co.uk. HTTPS is set up for you and renews on its own." />
      <Stepper current={0} />

      <Card>
        <form
          className="space-y-6 p-5 sm:p-6"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Field
            label="Domain"
            hint={
              cleaned ? (
                <>
                  We will use <strong className="font-medium text-text">{host}</strong>.
                </>
              ) : (
                "Just the address. No https:// needed."
              )
            }
            error={localErr ?? fieldErr}
            required
          >
            <Input
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="www.yourbusiness.co.uk"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              inputMode="url"
              autoFocus
              leading={<Globe />}
              className="[&_input]:h-11 [&_input]:font-mono [&_input]:text-md"
            />
          </Field>

          {isApex && (
            <div className="flex gap-3 rounded-lg border border-info/25 bg-info-soft p-4 text-sm">
              <Lightbulb className="mt-0.5 size-4 shrink-0 text-info-text" />
              <div className="text-text">
                <p>
                  <strong className="font-medium">Tip:</strong> {host} is the bare domain. Many registrars, including 123-reg, GoDaddy, IONOS and Fasthosts, cannot point it at an app directly.{" "}
                  <strong className="font-medium">www.{host}</strong> works everywhere, and you can forward the bare domain to it.
                </p>
                <Button type="button" size="sm" variant="secondary" className="mt-2.5" onClick={() => setRaw(`www.${host}`)}>
                  Use www.{host}
                </Button>
              </div>
            </div>
          )}

          <Field label="Send visitors to" hint="The web service that answers on this domain. You can change it later.">
            <Select
              value={service}
              onValueChange={setService}
              placeholder={instances.isPending ? "Loading services…" : "Choose a service"}
              options={web.map((w) => ({ value: w.name, label: w.name, description: w.urls?.public?.[0]?.replace(/^https:\/\//, "") ?? `${w.name}-${project}.${config.appDomain}` }))}
            />
          </Field>

          {add.error && !fieldErr ? <ErrorState error={add.error} /> : null}

          <div className="flex flex-col-reverse gap-3 border-t border-border pt-5 sm:flex-row sm:items-center">
            <ShowAsCode
              title={`Add ${host || "your domain"}`}
              cli={`guv domains add ${host || "www.example.co.uk"} --service ${service || "web"} --env ${env}`}
              manifestDiff={`services:\n  ${service || "web"}:\n    domains:\n+     - ${host || "www.example.co.uk"}`}
              apiCall={{ method: "POST", path: `/v1/projects/${project}/environments/${env}/domains`, body: { hostname: host || "www.example.co.uk", service: service || "web" } }}
            />
            <Button type="submit" variant="primary" size="lg" className="sm:ml-auto" loading={add.isPending} disabled={!service} iconRight={<ArrowRight />}>
              Continue
            </Button>
          </div>
        </form>
      </Card>

      <ul className="mt-6 grid gap-3 text-sm text-muted sm:grid-cols-3">
        <li className="flex gap-2">
          <Lock className="mt-0.5 size-4 shrink-0 text-subtle" /> Free HTTPS certificate, renewed automatically.
        </li>
        <li className="flex gap-2">
          <ShieldCheck className="mt-0.5 size-4 shrink-0 text-subtle" /> We check you own it before any traffic flows.
        </li>
        <li className="flex gap-2">
          <Globe className="mt-0.5 size-4 shrink-0 text-subtle" /> Your app stays on its {config.appDomain} address too.
        </li>
      </ul>
    </Page>
  );
}
